<template>
  <div ref="CREDIT_LIMIT_LIST">
    <section-header
        v-if="!hideTitle"
        :ressource="ressource"
        :is-extended="extended"
        :dialog="dialog"
        :fiche="fiche"
        @extended="$emit('extended', $event)"
        @close-dialog="$emit('closeDialog')">
    </section-header>

    <div class="credit-toolbar py-4">
      <v-row class="ma-0 filters-row" no-gutters>
        <v-col cols="auto" class="pr-2">
          <data-table-filter
              header-value="status"
              label="Statut"
              :column-config="statusColumnConfig"
              :current-filters="statusCurrentFilters"
              @apply="applyStatusFilter">
          </data-table-filter>
        </v-col>
      </v-row>
      <loader-button label="Ajouter" :disabled="false" @callback="add" />
    </div>

    <v-data-table
        class="credit-limit-table"
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        loading-text="Chargement des données"
        :items-per-page="-1"
        v-model:expanded="expanded"
        show-expand
        item-value="etablissementId"
        item-key="etablissementId"
        no-data-text="Aucun encours autorisé ou financement proposé"
        hide-default-footer>
      <template #[`item.data-table-expand`]="{ internalItem, isExpanded, toggleExpand }">
        <v-tooltip
            v-if="internalItem.raw.proposalPayrollCount > 1"
            :text="isExpanded(internalItem) ? 'Masquer le détail des paies' : 'Afficher le détail des paies'"
            location="top">
          <template #activator="{ props }">
            <v-btn
                v-bind="props"
                icon
                size="small"
                variant="tonal"
                color="primary"
                :aria-label="isExpanded(internalItem) ? 'Masquer le détail des paies' : 'Afficher le détail des paies'"
                @click="toggleExpand(internalItem)">
              <v-icon size="18">
                {{ isExpanded(internalItem) ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
              </v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </template>
      <template #[`item.etablissement`]="{ item }">
        <div class="credit-establishment">
          <strong data-testid="establishment-id">{{ item.etablissementId }}</strong> — {{ item.etablissement }}
        </div>
        <div class="credit-statuses">
          <v-chip v-if="item.authorized" size="x-small" color="secondary">Autorisé</v-chip>
          <v-chip v-if="item.proposed" size="x-small" color="primary">Proposé</v-chip>
          <v-chip
              v-if="item.inProgress"
              size="x-small"
              color="warning"
              data-testid="unlock-in-progress">
            Déblocage en cours
          </v-chip>
        </div>
      </template>
      <template #[`item.authorization`]="{ item }">
        <div class="credit-cell-stack">
          <strong data-testid="allocated-credit">Alloué : {{ formatCurrency(item.creditLimit) }}</strong>
          <span data-testid="consumed-credit">Consommé : {{ formatCurrency(item.consumedCredit) }}</span>
          <span>{{ item.authorized ? formatDate(item.creditLimitUntil) : 'Limite par défaut' }}</span>
        </div>
      </template>
      <template #[`item.proposal`]="{ item }">
        <div v-if="item.proposed" class="credit-cell-stack">
          <div>
            <strong data-testid="proposal-credit-limit">{{ formatCurrency(item.proposedCreditLimit) }}</strong>
            ·
            <span data-testid="proposal-payrolls">
              {{ item.proposalPayrollCount > 1 ? `${item.proposalPayrollCount} paies` : item.proposalPayrolls }}
            </span>
          </div>
          <span data-testid="proposal-reason">{{ item.proposalReason }}</span>
          <div
              v-if="item.proposalPayrollCount === 1 && item.proposalPayrollDetails.length === 1"
              class="credit-single-payroll">
            <span data-testid="proposal-payroll-employee">
              {{ item.proposalPayrollDetails[0].employee }}
            </span>
            <span data-testid="proposal-payroll-period">
              {{ item.proposalPayrollDetails[0].period }}
            </span>
          </div>
        </div>
        <span v-else>—</span>
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="credit-actions">
          <v-btn
              v-if="item.proposed"
              size="small"
              color="primary"
              data-testid="unlock-proposal-group"
              :loading="unlockingKey === `${item.etablissementId}:0`"
              @click="unlock(item)">
            {{ item.proposalPayrollCount > 1 ? 'Tout débloquer' : 'Débloquer' }}
          </v-btn>
          <v-btn v-if="item.authorized" size="small" @click="edit(item)">Modifier</v-btn>
          <v-btn
              v-if="item.authorized"
              size="small"
              variant="text"
              color="error"
              aria-label="Supprimer l’encours autorisé"
              :loading="resettingId === item.etablissementId"
              @click="reset(item)">
            Supprimer
          </v-btn>
        </div>
      </template>
      <template #expanded-row="{ columns, item }">
        <tr v-if="item.proposalPayrollCount > 1">
          <td :colspan="columns.length" class="pa-3 bg-grey-lighten-4">
            <div class="credit-payroll-list">
              <div
                  v-for="payroll in item.proposalPayrollDetails"
                  :key="payroll.paieId"
                  class="credit-payroll-detail"
                  data-testid="proposal-payroll-detail">
                <div class="credit-cell-stack">
                  <strong>Paie {{ payroll.codeCheque || payroll.paieId }}</strong>
                  <span>{{ payroll.employee }}</span>
                  <span data-testid="proposal-payroll-period">{{ payroll.period }}</span>
                </div>
                <div class="credit-cell-stack">
                  <span>À financer : <strong>{{ formatCurrency(payroll.amount) }}</strong></span>
                  <span>Encours nécessaire : <strong>{{ formatCurrency(payroll.requiredCreditLimit) }}</strong></span>
                </div>
                <div class="credit-payroll-reason">{{ payroll.reason }}</div>
                <v-btn
                    size="small"
                    color="primary"
                    :loading="unlockingKey === `${item.etablissementId}:${payroll.paieId}`"
                    :aria-label="`Débloquer la paie ${payroll.codeCheque || payroll.paieId}`"
                    @click="unlock(item, payroll.paieId)">
                  Débloquer
                </v-btn>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>

    <section-container
        v-if="dialogOpen"
        :section="section"
        :dialog-open="dialogOpen"
        :extended="true"
        :fiche="fiche"
        @close-dialog="closeEditor"
        @saved="saved">
    </section-container>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import AuthorizedCredit from '@/extend/classes/AuthorizedCredit';
import {Sections} from '@/generated/classes/AppSchema';
import {GenSection, RessourceGenSection} from '@/generated/ressources/generatedRessources';
import {DateFormat} from '@/framework/base/helpers/DateFormat';
import {getCurrency} from '@/framework/base/helpers/Localization';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import SectionContainer from '@/framework/base/components/SectionContainer.vue';
import DataTableFilter from '@/framework/base/components/DataTableFilter.vue';
import Section from '@/framework/base/classes/Section';
import SectionAuthorizedCredit from '@/extend/sections/SectionAuthorizedCreditExtend.vue';
import {ColumnConfig, ListChoix} from '@/generated/classes/ListData';

const STATUS_PROPOSED = 1;
const STATUS_AUTHORIZED = 2;
const STATUS_IN_PROGRESS = 3;

export default defineComponent({
  name: 'SectionCreditLimitList',
  components: {DataTableFilter, LoaderButton, SectionContainer, SectionHeader},
  props: {
    hideTitle: {type: Boolean, default: false},
    extended: {type: Boolean, default: false},
    dialog: {type: Boolean, default: false},
    fiche: {type: String, required: true},
  },
  emits: ['extended', 'closeDialog'],
  data() {
    return {
      sectionName: Sections.CREDIT_LIMIT_LIST,
      items: [] as AuthorizedCredit[],
      statusFilters: [] as ListChoix[],
      expanded: [] as number[],
      loading: false,
      resettingId: 0,
      unlockingKey: '',
      dialogOpen: false,
      selected: new AuthorizedCredit(),
      headers: [
        {title: 'Établissement', value: 'etablissement', sortable: true, width: '28%'},
        {title: 'Autorisation actuelle', value: 'authorization', sortable: false, width: '18%'},
        {title: 'Proposition', value: 'proposal', sortable: false, width: '32%'},
        {title: '', value: 'actions', sortable: false, align: 'end', width: '22%'},
      ],
    };
  },
  computed: {
    filteredItems(): AuthorizedCredit[] {
      if (this.statusFilters.length === 0)
        return this.items;
      return this.items.filter(item => this.statusFilters.some(filter =>
        (filter.value === STATUS_PROPOSED && item.proposed) ||
        (filter.value === STATUS_AUTHORIZED && item.authorized) ||
        (filter.value === STATUS_IN_PROGRESS && item.inProgress),
      ));
    },
    statusColumnConfig(): ColumnConfig {
      const config = new ColumnConfig();
      config.name = 'status';
      config.isFilterable = true;
      const proposed = new ListChoix();
      proposed.value = STATUS_PROPOSED;
      proposed.text = 'Proposées';
      proposed.count = this.items.filter(item => item.proposed).length;
      const authorized = new ListChoix();
      authorized.value = STATUS_AUTHORIZED;
      authorized.text = 'Actives';
      authorized.count = this.items.filter(item => item.authorized).length;
      const inProgress = new ListChoix();
      inProgress.value = STATUS_IN_PROGRESS;
      inProgress.text = 'Déblocages en cours';
      inProgress.count = this.items.filter(item => item.inProgress).length;
      config.choix = [proposed, authorized, inProgress];
      return config;
    },
    statusCurrentFilters(): ListChoix[] {
      return this.statusFilters;
    },
    section(): Section {
      const section = new Section();
      section.component = SectionAuthorizedCredit;
      section.type = 'authorizedCredit';
      section.data = this.selected;
      section.ressource = RessourceGenSection(GenSection.AUTHORIZED_CREDIT);
      return section;
    },
    ressource() {
      return RessourceGenSection(GenSection.CREDIT_LIMIT_LIST);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    applyStatusFilter(filters: ListChoix[]): void {
      this.statusFilters = filters;
    },
    async load(): Promise<void> {
      this.loading = true;
      try {
        const items = await AuthorizedCredit.list();
        AuthorizedCredit.getDate(items);
        this.items = items;
      } finally {
        this.loading = false;
      }
    },
    add(): void {
      this.selected = new AuthorizedCredit();
      this.selected.id = -1;
      this.selected.creditLimit = 0;
      this.selected.permanent = false;
      this.dialogOpen = true;
    },
    edit(item: AuthorizedCredit): void {
      this.selected = item;
      this.dialogOpen = true;
    },
    closeEditor(): void {
      this.dialogOpen = false;
    },
    saved(): void {
      this.dialogOpen = false;
      this.load();
    },
    async reset(item: AuthorizedCredit): Promise<void> {
      this.resettingId = item.etablissementId;
      try {
        await AuthorizedCredit.reset(item);
        await this.load();
      } finally {
        this.resettingId = 0;
      }
    },
    async unlock(item: AuthorizedCredit, paieId = 0): Promise<void> {
      this.unlockingKey = `${item.etablissementId}:${paieId}`;
      try {
        const request = item.cloneAuthorizedCredit();
        request.proposalPayrollId = paieId;
        await AuthorizedCredit.unlock(request);
        await this.load();
      } finally {
        this.unlockingKey = '';
      }
    },
    formatDate(value: Date | null): string {
      return value ? DateFormat.getDateLabel(value) : 'Sans échéance';
    },
    formatCurrency(value: number): string {
      return getCurrency(value);
    },
  },
});
</script>

<style scoped>
.credit-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.credit-toolbar .filters-row {
  flex: 1 1 auto;
}

.credit-limit-table :deep(.v-table__wrapper) {
  overflow-x: hidden;
}

.credit-limit-table :deep(table) {
  table-layout: fixed;
  width: 100%;
}

.credit-limit-table :deep(th),
.credit-limit-table :deep(td) {
  white-space: normal;
  overflow-wrap: anywhere;
}

.credit-establishment {
  line-height: 1.35;
}

.credit-statuses,
.credit-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.credit-statuses {
  margin-top: 4px;
}

.credit-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.credit-single-payroll {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  color: rgb(var(--v-theme-secondary));
}

.credit-actions {
  justify-content: flex-end;
  gap: 8px;
}

.credit-payroll-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.credit-payroll-detail {
  display: grid;
  grid-template-columns: minmax(160px, 1.2fr) minmax(190px, 1fr) minmax(160px, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: white;
  border-radius: 4px;
}

.credit-payroll-reason {
  min-width: 0;
  overflow-wrap: anywhere;
}

@media (max-width: 1100px) {
  .credit-payroll-detail {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .credit-payroll-detail > .v-btn {
    justify-self: end;
  }
}
</style>
